import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Terminal, Github, Linkedin, FileText, ArrowDown, Shield, Cloud, Lock, Server } from "lucide-react";
import { personalInfo } from "../data/mock";
import ParticleBackground from "./ParticleBackground";

const Hero = () => {
  const [terminalText, setTerminalText] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  const [commandComplete, setCommandComplete] = useState(false);

  const command = "git clone https://github.com/theankitojha/portfolio.git";
  const response = `Cloning into 'portfolio'...\nremote: Enumerating objects: 4+, done.\nremote: Total 4+ years of DevSecOps experience\nUnpacking objects: 100%\n\n>> Welcome! I'm ${personalInfo.name}\n>> ${personalInfo.title}\n>> ${personalInfo.tagline}\n\n[✓] Ready to architect secure infrastructure`;

  useEffect(() => {
    let index = 0;
    const fullText = `$ ${command}\n${response}`;
    
    const typingInterval = setInterval(() => {
      if (index < fullText.length) {
        setTerminalText(fullText.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typingInterval);
        setCommandComplete(true);
      }
    }, 30);

    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 500);

    return () => {
      clearInterval(typingInterval);
      clearInterval(cursorInterval);
    };
  }, []);

  return (
    <section
      id="hero"
      className="grid-background"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "120px 7.6923% 60px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Particle Background */}
      <ParticleBackground />

      {/* Floating Cloud/Security Icons - More Subtle */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.08 }}
        transition={{ duration: 1, delay: 1 }}
        style={{
          position: "absolute",
          top: "15%",
          right: "10%",
          zIndex: 1,
        }}
        className="floating-cloud"
      >
        <Cloud size={80} color="var(--brand-primary)" strokeWidth={0.5} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.08 }}
        transition={{ duration: 1, delay: 1.2 }}
        style={{
          position: "absolute",
          bottom: "20%",
          left: "8%",
          zIndex: 1,
        }}
        className="floating-cloud"
      >
        <Shield size={70} color="var(--brand-primary)" strokeWidth={0.5} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.08 }}
        transition={{ duration: 1, delay: 1.4 }}
        style={{
          position: "absolute",
          top: "25%",
          left: "12%",
          zIndex: 1,
        }}
        className="floating-cloud"
      >
        <Lock size={60} color="var(--brand-primary)" strokeWidth={0.5} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.08 }}
        transition={{ duration: 1, delay: 1.6 }}
        style={{
          position: "absolute",
          bottom: "15%",
          right: "15%",
          zIndex: 1,
        }}
        className="floating-cloud"
      >
        <Server size={75} color="var(--brand-primary)" strokeWidth={0.5} />
      </motion.div>
      <div
        style={{
          maxWidth: "1200px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "40px",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Terminal Window */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="glass hover-lift"
          style={{
            borderRadius: "0px",
            overflow: "hidden",
            border: "1px solid var(--border-medium)",
          }}
        >
          {/* Terminal Header */}
          <div
            style={{
              background: "rgba(0, 255, 209, 0.05)",
              padding: "12px 20px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              borderBottom: "1px solid var(--border-subtle)",
            }}
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{ width: "12px", height: "12px", borderRadius: "50%", background: "#FF5F56" }}
            />
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
              style={{ width: "12px", height: "12px", borderRadius: "50%", background: "#FFBD2E" }}
            />
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
              style={{ width: "12px", height: "12px", borderRadius: "50%", background: "#27C93F" }}
            />
            <Terminal size={16} color="var(--brand-primary)" style={{ marginLeft: "12px" }} />
            <span style={{ fontSize: "14px", color: "var(--brand-primary)", fontWeight: 500 }}>secure-terminal.sh</span>
          </div>

          {/* Terminal Body with Scanline Effect */}
          <div
            style={{
              padding: "30px",
              fontFamily: "'Courier New', monospace",
              fontSize: "16px",
              lineHeight: "1.8",
              minHeight: "400px",
              color: "var(--text-primary)",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              position: "relative",
              background: "rgba(0, 10, 10, 0.4)",
            }}
          >
            {/* Subtle Scanline Overlay */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 255, 209, 0.03) 2px, rgba(0, 255, 209, 0.03) 4px)",
                pointerEvents: "none",
              }}
            />
            
            {terminalText.split("\n").map((line, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                {line.startsWith("$") ? (
                  <span style={{ color: "var(--brand-primary)" }}>{line}</span>
                ) : line.startsWith(">>") ? (
                  <span style={{ color: "var(--brand-primary)", fontWeight: 600, fontSize: "18px" }}>{line}</span>
                ) : line.startsWith("[✓]") ? (
                  <span style={{ color: "var(--success)" }}>{line}</span>
                ) : (
                  <span style={{ color: "var(--text-secondary)" }}>{line}</span>
                )}
              </motion.div>
            ))}
            {showCursor && <span className="terminal-cursor" />}
          </div>
        </motion.div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: commandComplete ? 1 : 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <a
            href={personalInfo.github}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
            style={{
              textDecoration: "none",
              padding: "12px 20px",
              minHeight: "auto",
            }}
          >
            <Github size={20} />
            GitHub
          </a>
          <a
            href={personalInfo.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
            style={{
              textDecoration: "none",
              padding: "12px 20px",
              minHeight: "auto",
            }}
          >
            <Linkedin size={20} />
            LinkedIn
          </a>
          <a
            href={personalInfo.medium}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
            style={{
              textDecoration: "none",
              padding: "12px 20px",
              minHeight: "auto",
            }}
          >
            <FileText size={20} />
            Medium
          </a>
          <a
            href="#contact"
            className="btn-primary"
            style={{
              textDecoration: "none",
              padding: "12px 20px",
              minHeight: "auto",
            }}
          >
            Get in Touch
          </a>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: commandComplete ? 1 : 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        style={{
          position: "absolute",
          bottom: "40px",
          left: "50%",
          transform: "translateX(-50%)",
          animation: "pulse 2s infinite",
        }}
      >
        <ArrowDown size={24} color="var(--brand-primary)" />
      </motion.div>
    </section>
  );
};

export default Hero;
